import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Upload, RefreshCw, AlertCircle } from 'lucide-react';
import Card from './Card';
import Button from './Button';
import Spinner from './Spinner';
import ConfirmModal from './ConfirmModal';
import syncService from '../../services/syncService';
import { readWorkbook } from '../../utils/xlsx';
import { downloadCodeWorkbook, parseCodeWorkbook } from '../../utils/conversionCodeWorkbook';
import { formatDateTime } from '../../utils/datetime';

// THE ACCOUNTING CODE MAP — the codes a property uses, and what each one becomes.
//
// NOT the Conversions screen, deliberately. A conversion is a pair (this source value means that target
// value) and the screen for it is built around adding pairs. A code here is a ROW: ten values that must
// all be present — accounting code, GL account, currency, category, charge category, account type — and
// two dozen more the property's team keeps for their own working. That does not fit a two-column editor,
// and bending one into the other would leave both worse.
//
// THE WORK HAPPENS IN EXCEL. Nobody types 400 GL accounts into a web page. The transaction codes arrive
// from the property when the instruction runs; somebody downloads them, fills the map in, and uploads the
// finished file. So the screen is a viewer with two buttons, and the file is the editor.
//
// THE COLUMNS COME FROM THE SERVER with the rows, and the workbook and its check are built from that one
// list (utils/conversionCodeWorkbook.js) — never from a copy kept here.

const CHIP = {
    marginLeft: 8, padding: '0.1rem 0.45rem', borderRadius: 999,
    background: '#f1f5f9', color: '#64748b', fontSize: '0.75rem', fontWeight: 600,
};

const ConversionCodesPanel = ({ syncId, taskId, showToast }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ codes: [], total: 0, columns: [], contextColumns: [], scopeType: null });
    const [search, setSearch] = useState('');
    const [uploading, setUploading] = useState(false);
    const [pending, setPending] = useState(null);   // a parsed file waiting to be confirmed
    const [problem, setProblem] = useState(null);   // why a file was refused
    const fileInput = useRef(null);

    // Which tab of the task page this table is on, so an edit returns to it rather than to the default.
    const fromTab = new URLSearchParams(location.search).get('from') || '';

    // THE TOAST IS HELD IN A REF, AND THAT IS NOT FUSSINESS.
    //
    // `showToast` is redefined on every render of the task page (it is a plain function, not a
    // useCallback), so naming it as a dependency of `load` gives `load` a new identity on every parent
    // render — and the effect below, which depends on `load`, then re-runs and re-fetches. The task page
    // re-renders on its own 3-second auto-refresh timer, so the codes were being reloaded on that tick
    // for ever: a screen that looked like it was refreshing itself, and an API call every few seconds
    // for data nobody had asked for again.
    //
    // The ref keeps the latest function available without making it part of the identity of `load`,
    // which now changes only when the project or task does.
    const toastRef = useRef(showToast);
    useEffect(() => { toastRef.current = showToast; });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await syncService.getConversionCodeRecords(syncId, taskId, { limit: 5000 });
            setData({
                codes: Array.isArray(res?.codes) ? res.codes : [],
                total: res?.total || 0,
                columns: Array.isArray(res?.columns) ? res.columns : [],
                contextColumns: Array.isArray(res?.contextColumns) ? res.contextColumns : [],
                scopeType: res?.scopeType || null,
            });
        } catch (err) {
            console.error('Error loading conversion codes:', err);
            toastRef.current?.('Failed to load the codes', 'error');
        } finally {
            setLoading(false);
        }
    }, [syncId, taskId]);

    useEffect(() => { load(); }, [load]);

    // Filtering is local: the whole map is already here, and a round trip per keystroke would be slower
    // and no more accurate.
    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return data.codes;
        return data.codes.filter(c => data.columns.some(col => String(c[col.field] || '').toLowerCase().includes(q)));
    }, [data, search]);

    // A read that did not bring everything back. Anything built from it would be missing rows.
    const truncated = data.total > data.codes.length;

    const handleDownload = async () => {
        try {
            await downloadCodeWorkbook({
                columns: data.columns,
                contextColumns: data.contextColumns,
                codes: data.codes,
                filename: `${data.scopeType || 'accounting-codes'}-${syncId}.xlsx`,
            });
        } catch (err) {
            console.error('Error building the workbook:', err);
            showToast?.('The workbook could not be built', 'error');
        }
    };

    // READ AND CHECKED BEFORE ANYTHING IS SENT. A file with a blank required value never reaches the
    // server, and what it is missing is named row by row rather than as "invalid file".
    const handleFile = async (file) => {
        if (!file) return;
        setProblem(null);
        setPending(null);
        try {
            const book = await readWorkbook(file);
            const parsed = parseCodeWorkbook(book, { columns: data.columns, contextColumns: data.contextColumns });
            if (parsed.missingColumns.length) {
                setProblem({
                    title: `${file.name} is missing ${parsed.missingColumns.length} required column${parsed.missingColumns.length === 1 ? '' : 's'}`,
                    lines: parsed.missingColumns,
                    hint: 'Download the workbook above and fill that one in — its headers are the ones this expects.',
                });
                return;
            }
            if (parsed.errors.length) {
                setProblem({
                    title: `${file.name} has ${parsed.errors.length} row${parsed.errors.length === 1 ? '' : 's'} that cannot be accepted — nothing has been changed`,
                    lines: parsed.errors.slice(0, 25),
                    more: Math.max(0, parsed.errors.length - 25),
                    hint: 'Every required column must have a value on every row.',
                });
                return;
            }
            setPending({ file: file.name, rows: parsed.rows, ignored: parsed.ignored });
        } catch (err) {
            setProblem({ title: err.message || 'The file could not be read.', lines: [] });
        }
    };

    const handleUpload = async () => {
        if (!pending) return;
        setUploading(true);
        try {
            const res = await syncService.replaceConversionCodeRecords(syncId, taskId, pending.rows);
            showToast?.(res?.message || `${pending.rows.length} code(s) uploaded`, 'success');
            setPending(null);
            await load();
        } catch (err) {
            console.error('Error uploading codes:', err);
            showToast?.(err.response?.data?.message || 'The upload was refused', 'error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Card>
            <div className="card-header">
                <h2>Accounting Codes</h2>
                <div className="header-actions">
                    <Button variant="secondary" onClick={load} disabled={loading}>
                        <RefreshCw className={`icon-sm ${loading ? 'spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="secondary" onClick={handleDownload} disabled={!data.codes.length || truncated}>
                        <Download className="icon-sm" />
                        Download Excel
                    </Button>
                    <Button variant="secondary" onClick={() => fileInput.current?.click()} disabled={uploading || truncated || !data.columns.length}>
                        <Upload className="icon-sm" />
                        Upload Excel
                    </Button>
                    <input
                        ref={fileInput}
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: 'none' }}
                        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                    />
                </div>
            </div>

            <div className="conversion-filters">
                <div className="filter-group filter-search">
                    <label>Search</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search codes..."
                        className="form-input"
                    />
                </div>
            </div>

            {/* WHY A FILE WAS REFUSED, in full. "Invalid file" costs somebody an hour of guessing. */}
            {problem && (
                <div style={{ margin: '0 1.25rem 1rem', padding: '0.75rem 1rem', borderRadius: 8,
                              background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.875rem' }}>
                    <strong>{problem.title}</strong>
                    {problem.lines?.length > 0 && (
                        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
                            {problem.lines.map((l, i) => <li key={i}>{l}</li>)}
                            {problem.more > 0 && <li>and {problem.more} more</li>}
                        </ul>
                    )}
                    {problem.hint && <p style={{ margin: '0.5rem 0 0', color: '#7f1d1d' }}>{problem.hint}</p>}
                </div>
            )}

            {/* MORE CODES THAN CAME BACK. The workbook is built from what is on this screen and an upload
                replaces everything, so a partial list would hand somebody a file whose upload deletes the
                rows it never showed them. Both buttons go until it can be read whole. */}
            {truncated && (
                <div style={{ margin: '0 1.25rem 1rem', padding: '0.75rem 1rem', borderRadius: 8,
                              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.875rem' }}>
                    This project has {data.total.toLocaleString()} codes and only {data.codes.length.toLocaleString()} could
                    be read at once, so the workbook would be incomplete — downloading and uploading are
                    switched off until that is raised.
                </div>
            )}

            {loading && !data.codes.length ? (
                <div className="loading-section">
                    <Spinner size="md" />
                    <p>Loading codes...</p>
                </div>
            ) : !data.codes.length ? (
                <div className="empty-state">
                    <AlertCircle className="empty-icon" />
                    <p>
                        No codes yet. Send the instruction to the source — the transaction codes it answers
                        with are listed here, ready to be downloaded and mapped.
                    </p>
                </div>
            ) : (
                <div className="conversions-table-wrapper">
                    <p className="form-hint" style={{ margin: '0 0 0.5rem' }}>
                        {data.total.toLocaleString()} code{data.total === 1 ? '' : 's'}
                        {search && shown.length !== data.codes.length ? ` · ${shown.length.toLocaleString()} matching` : ''}.
                        Running the instruction again refreshes the codes and their descriptions and leaves
                        the mapping alone; uploading a workbook <strong>replaces</strong> the map with what
                        the file contains. Select a row to edit that code on its own.
                    </p>
                    <table className="conversions-table">
                        <thead>
                            <tr>
                                {data.columns.map(col => <th key={col.field}>{col.header}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map(code => (
                                // ONE ROW, ONE PAGE. A full page rather than an overlay (STYLE.md "No
                                // Modals"): it has a URL to send to somebody, a working back button, and
                                // room for the thirty-four fields a code carries. `from` rides along so
                                // Cancel and Save return to the tab this table is on.
                                <tr
                                    key={code.id}
                                    onClick={() => navigate(
                                        `/sync/${syncId}/task/${taskId}/code/${encodeURIComponent(code.code)}`
                                        + `${fromTab ? `?from=${encodeURIComponent(fromTab)}` : ''}`,
                                        // The row travels with the navigation so the editor can open on it
                                        // immediately. It still re-reads from the server; this only spares
                                        // it a dependency on the list search matching the code back.
                                        //
                                        // `siblings` is what makes Previous/Next on the editor walk the
                                        // list the person is actually looking at — this order, this
                                        // search — rather than a second ordering the server invents.
                                        { state: { codeRow: code, siblings: shown.map(c => c.code) } })}
                                    onKeyDown={(e) => {
                                        // Reachable without a mouse: the row is the control, so it answers
                                        // to Enter and Space the way a button does.
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/sync/${syncId}/task/${taskId}/code/${encodeURIComponent(code.code)}`
                                                + `${fromTab ? `?from=${encodeURIComponent(fromTab)}` : ''}`);
                                        }
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    title={`Edit ${code.code}`}
                                    style={{
                                        cursor: 'pointer',
                                        ...(code.still_returned === 'N' ? { color: '#9ca3af' } : {}),
                                    }}
                                >
                                    {data.columns.map((col, i) => (
                                        <td key={col.field}>
                                            {code[col.field] || <span style={{ color: '#9ca3af' }}>—</span>}
                                            {/* WITHDRAWN, NOT GONE. The property has stopped returning this code; the
                                                mapping somebody made against it stays, and the date says when — which
                                                is the question asked when a code stops matching anything. */}
                                            {i === 0 && code.still_returned === 'N' && (
                                                <span style={CHIP} title={code.withdrawn_at ? `Last returned ${formatDateTime(code.withdrawn_at)}` : undefined}>
                                                    no longer returned
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* AN UPLOAD REPLACES THE MAP, so it is confirmed with the two numbers that matter: what is
                coming in, and what is there now. */}
            {pending && (
                <ConfirmModal
                    title="Replace the accounting code map?"
                    message={`${pending.file} has ${pending.rows.length} row${pending.rows.length === 1 ? '' : 's'}. `
                        + `It replaces the ${data.total} code${data.total === 1 ? '' : 's'} stored now — any code not in the file is removed.`
                        + (pending.ignored?.length ? ` Columns not recognised are left out: ${pending.ignored.join(', ')}.` : '')}
                    confirmLabel={uploading ? 'Uploading…' : 'Replace'}
                    danger
                    onConfirm={handleUpload}
                    onCancel={() => { if (!uploading) setPending(null); }}
                />
            )}
        </Card>
    );
};

export default ConversionCodesPanel;
